import { CircularProgress } from '@sortlist-frontend/design-system';
import { useState } from 'react';

import { RichText } from '_features/RankingPage/components/RichText';
import type { RankingRichText } from '_features/RankingPage/types';

import { ToggleItem } from './ToggleItem';

interface ChecklistProps {
  items: readonly RankingRichText[];
  done: string;
  rest: string;
  empty: string;
}

const richTextKey = (text: RankingRichText) =>
  text.map((part) => (typeof part === 'string' ? part : part.text)).join('');

export const Checklist = (props: ChecklistProps) => {
  const { items, done, rest, empty } = props;
  const [checked, setChecked] = useState<readonly boolean[]>(items.map(() => false));
  const count = checked.filter(Boolean).length;
  const message = count === items.length ? done : count === 0 ? empty : `${items.length - count} point(s) ${rest}`;

  return (
    <div className="grid items-start gap-6 md:grid-cols-[1fr_12rem]">
      <div className="space-y-3">
        {items.map((item, index) => (
          <ToggleItem
            key={richTextKey(item)}
            checked={checked[index]}
            onChange={(value) => setChecked(checked.map((current, i) => (i === index ? value : current)))}>
            <RichText text={item} />
          </ToggleItem>
        ))}
      </div>
      <div className="flex flex-col items-center rounded-2xl border border-secondary-200 bg-white p-5 text-center">
        <CircularProgress
          aria-label={`${count}/${items.length}`}
          value={items.length === 0 ? 0 : (count / items.length) * 100}
          color="primary"
          size="xl"
        />
        <p className="mt-2 text-2xl font-extrabold text-secondary-950" aria-live="polite">
          {count}/{items.length}
        </p>
        <p className="mt-1 text-xs text-secondary-500">{message}</p>
      </div>
    </div>
  );
};
